// App.js (Incorporating the full text outline)
import React, { useState } from "react";
import { useForm } from '@formspree/react';

import "./App.css";

// FAQItem component remains the same
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="faq-item">
      <div className="faq-question" onClick={() => setIsOpen(!isOpen)}>
        <h3>{question}</h3>
        <span>{isOpen ? "-" : "+"}</span>
      </div>
      {isOpen && <div className="faq-answer"><p>{answer}</p></div>}
    </div>
  );
};

function App() {
  const [contactFormState, handleContactSubmit] = useForm("xqaqdekk"); // Your Formspree ID for the general contact form
  const [formData, setFormData] = useState({ email: '', message: '' });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Updated "Why Choose Us" data based on our outline
  const whyChooseUsData = [
    {
      icon: "💖", // Replace with actual icons (e.g., from an icon library)
      title: "Familiar Voice Impact",
      description: "Our companions use a loved one's true voice, creating a unique emotional anchor that measurably reduces resident agitation and fosters deeper, more genuine engagement than any generic AI."
    },
    {
      icon: "🤝",
      title: "Empower Your Care Team",
      description: "Offer residents meaningful, on-demand engagement with a familiar presence, providing comfort and interaction when staff are attending to other duties. This helps manage resident needs effectively and gives your team more flexibility."
    },
    {
      icon: "😊", // Or a sun icon for "Better Days"
      title: "Better Resident Days",
      description: "Provide a soothing, engaging activity that can help ease restlessness and anxiety, particularly during challenging periods like late-afternoon sundowning. This leads to calmer transitions and more peaceful days for everyone."
    },
    {
      icon: "✨",
      title: "Forward-Thinking Care",
      description: "Differentiate your facility by adopting an innovative, deeply personal approach to memory care that modern families value. Show your commitment to leveraging compassionate technology for enhanced resident quality of life."
    }
  ];

  // Placeholder for reviewsData - uncomment and populate when you have facility testimonials
  /*
  const reviewsData = [
    {
      quote: "Since implementing Familiar Voices, we've seen a remarkable improvement in resident engagement...",
      name: "[Name of Director/Administrator]",
      role: "[e.g., Director of Memory Care, [Facility Name]]",
      // photoUrl: "/images/placeholder-facility-user1.png"
    },
    // Add 1-2 more when available
  ];
  */

  // Updated FAQ data based on our outline
  const faqData = [
    {
      question: "How quickly can a \"Familiar Companion\" be set up for a resident?",
      answer: "Setup is remarkably fast! Once your facility is onboarded and we receive a short voice recording from a family member (a simple process we guide them through), their loved one's personalized Digital Twin companion can be created and ready for interaction within minutes. Our team fully supports your staff through the easy initial setup."
    },
    {
      question: "What technology does our facility need to provide?",
      answer: "Familiar Voices runs on standard tablets (iOS or Android). We can provide pre-configured tablets for pilot programs if needed. A stable Wi-Fi connection throughout your resident areas is the primary requirement."
    },
    {
      question: "How does Familiar Voices ensure resident privacy and data security?",
      answer: "We are committed to the highest data security standards, designing our platform with HIPAA compliance considerations. All voice data and personal information are encrypted and securely managed, with clear family consent protocols for creating each Digital Twin."
    },
    {
      question: "What is the time commitment for our staff to manage the system?",
      answer: "Familiar Voices is designed to be a supportive tool, not an added burden. Our team handles initial setup and family coordination. Daily use involves minimal staff time – often just helping a resident start a session on a tablet, similar to other engagement activities."
    },
    {
      question: "How are the companion interactions kept appropriate and beneficial?",
      answer: "Our Digital Twin interactions are specifically designed for gentle, comforting, and positive engagement, guided by reminiscence therapy principles. The AI operates with a carefully crafted context prompt to ensure conversations remain supportive, appropriate for memory care residents, and focused on providing a reassuring presence."
    }
  ];


  return (
    <div className="App">
      {/* Header Section */}
      <header className="header">
        <div className="container header-container">
          <div className="logo">
            <h1>Familiar Voices</h1>
          </div>
          <nav className="main-nav">
            <a href="#about">About</a>
            <a href="#features">Features</a>
            <a href="#why-choose-us">Why Us</a> {/* Changed from "For Facilities" to match section ID */}
            <a href="/for-families">For Families</a> {/* Ensure you create this route/page or an anchor */}
            <a href="#faq">FAQ</a>
          </nav>
          <a
            href="https://calendar.app.google/rtoHyXnsNAJToZEy9" // YOUR SCHEDULING LINK
            className="btn btn-primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Schedule a Demo
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <h2>Reassurance and Comfort, Reimagined for Memory Care.</h2>
              <p className="hero-text">
                Familiar Voices provides 24/7 access to comforting companions featuring the trusted voices of family members. This support helps reduce agitation, ease loneliness, and enhance daily well-being, while providing support to your care team and peace of mind for families.
              </p>
              <div className="hero-cta-buttons" style={{ marginTop: "30px" }}>
                <a
                  href="https://calendar.app.google/rtoHyXnsNAJToZEy9" // YOUR SCHEDULING LINK
                  className="btn btn-primary btn-large"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Schedule a Free 30-Day Pilot
                </a>
                {/* Fallback text link removed as per your request */}
              </div>
            </div>
            <div className="mockup-image">
              <img
                src="/images/tabletandphone.png"
                alt="Familiar Voices app interface on tablet and phone"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <h2 className="section-title">What is Familiar Voices? Reconnecting Through a Familiar Presence.</h2>
          <p className="section-text">
            Familiar Voices is a pioneering platform for memory care communities. We use advanced AI to create an interactive "Digital Twin" that embodies the unique voice and essence of a resident's loved one. This 'Familiar Companion' offers on-demand comfort through gentle conversations, shares cherished stories, and provides personalized reminders, significantly enhancing quality of life and offering invaluable support to your care team.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <h2 className="section-title">Core Features Designed for Your Facility & Residents</h2>
          <div className="features-grid">
            {[
              {
                title: "Authentic \"Digital Twin\" Voice Creation",
                description: "Our AI meticulously crafts an interactive Digital Twin from a loved one's voice. This creates a uniquely comforting and trusted presence, far more engaging for residents than generic voice assistants."
              },
              {
                title: "Personalized Comfort & Reminiscence",
                description: "Digital Twins engage residents by recounting cherished family stories and shared memories, or providing gentle, personalized reminders in that familiar voice. This actively supports reminiscence therapy principles."
              },
              {
                title: "On-Demand Support & Companionship",
                description: "Provide a soothing presence anytime a resident needs reassurance, helping to reduce agitation and loneliness—especially valuable when staff are attending to other critical needs."
              },
              {
                title: "Simple for Staff & Residents",
                description: "Our intuitive tablet interface is designed for ease of use by residents with cognitive challenges and for quick facilitation by your care staff with minimal training."
              },
              {
                title: "Secure & Ethical Platform",
                description: "Built with data privacy and ethical considerations at its core (designed with HIPAA compliance in mind). We prioritize family consent and resident dignity in every Digital Twin interaction."
              },
              {
                title: "Enhances Your Care Offering",
                description: "Familiar Voices complements your existing care plans, offering a unique, non-pharmacological tool to demonstrably improve resident emotional well-being and support staff."
              }
            ].map((feature, index) => (
              <div key={index} className="feature-item">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-choose-us" className="why-choose-us-section">
        <div className="container">
          <h2 className="section-title">Why Partner with Familiar Voices for Your Memory Care Community?</h2>
          <p className="section-text">Choosing Familiar Voices means investing in a solution that understands the profound need for connection in memory care. We offer more than just technology; we provide a pathway to enhanced resident well-being and empowered staff.</p>
          <div className="why-choose-us-grid">
            {whyChooseUsData.map((item, index) => ( // Using the updated whyChooseUsData from above
              <div key={index} className="why-item">
                <div className="why-item-icon">{item.icon}</div> {/* Replace with actual icon components or img tags */}
                <div className="why-item-content">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section - Commented out until you have facility testimonials */}
      {/*
      <section id="reviews" className="reviews-section">
        <div className="container">
          <h2 className="section-title">Trusted by Facilities & Valued by Families</h2>
          <div className="reviews-grid">
            {reviewsData && reviewsData.map((review, index) => (
              <div key={index} className="review-card">
                <p className="review-quote">"{review.quote}"</p>
                <div className="reviewer-info">
                  {review.photoUrl && <img src={review.photoUrl} alt={review.name} className="reviewer-photo" />}
                  {!review.photoUrl && <div className="reviewer-photo-placeholder"></div>}
                  <div className="reviewer-details">
                    <p className="reviewer-name">{review.name}</p>
                    <p className="reviewer-role">{review.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="container">
          <h2 className="section-title">Your Questions Answered: Familiar Voices for Facilities</h2>
          <div className="faq-list">
            {faqData.map((faq, index) => ( // Using the updated faqData from above
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section - With Scheduling Link & Fallback Contact Form */}
      <section className="cta">
        <div className="container">
          <h2 className="section-title">Ready to Transform Resident Engagement in Your Memory Care Facility?</h2>
          <p className="section-text">
            Discover the profound impact of Familiar Voices. Schedule a brief, no-obligation demo to see how our Familiar Companions, powered by Digital Twin technology, can bring comfort to your residents and support your dedicated team. Let's discuss a free pilot program tailored to your needs.
          </p>
          <a
            href="https://calendar.app.google/rtoHyXnsNAJToZEy9" // YOUR SCHEDULING LINK
            className="btn btn-primary btn-large"
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginBottom: "20px" }} // Add space before the "Or" text
          >
            Schedule Your Free Demo & Pilot Discussion
          </a>
          <p className="section-text" style={{marginTop: "20px", marginBottom: "10px", fontSize: "1rem"}}>
            Or, have specific questions before scheduling? Send us a message below.
          </p>
          {contactFormState.succeeded ? (
            <p className="success-message" style={{ color: '#1e663d', fontWeight: 'bold', textAlign: 'center', fontSize: '1.2em', backgroundColor: '#e6f4ea', padding: '1rem', borderRadius: '5px', border: '1px solid #b3dbc0' }}>
              Thanks for your message! We'll be in touch soon.
            </p>
          ) : (
            <form onSubmit={handleContactSubmit} className="contact-form" style={{ maxWidth: '600px', margin: '0 auto', backgroundColor: '#fff8e7', padding: '20px', borderRadius: '8px', border: '1px solid #ebc99d' }}>
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label htmlFor="email-contact" style={{ display: 'block', marginBottom: '5px', textAlign: 'left', color: '#5a3d2b', fontWeight: '600' }}>Your Email</label>
                <input
                  id="email-contact" // Changed id to avoid conflict if another email field exists
                  type="email"
                  name="email" // Formspree field name
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{ width: '100%', padding: '12px', border: '1px solid #ebc99d', borderRadius: '5px', fontSize: '1rem' }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="message-contact" style={{ display: 'block', marginBottom: '5px', textAlign: 'left', color: '#5a3d2b', fontWeight: '600' }}>Your Message/Questions</label>
                <textarea
                  id="message-contact" // Changed id
                  name="message" // Formspree field name
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="5" // Increased rows
                  style={{ width: '100%', padding: '12px', border: '1px solid #ebc99d', borderRadius: '5px', fontSize: '1rem' }}
                />
              </div>
              <button type="submit" disabled={contactFormState.submitting} className="btn btn-primary btn-large" style={{ width: '100%', padding: '12px' }}>
                {contactFormState.submitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-links">
              <div className="footer-section">
                <h3>For Facilities</h3>
                <a href="#why-choose-us">Why Familiar Voices</a>
                <a href="#features">Features</a>
                <a
                  href="https://calendar.app.google/rtoHyXnsNAJToZEy9" // YOUR SCHEDULING LINK
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Request a Pilot Demo
                </a>
              </div>
              <div className="footer-section">
                <h3>For Families</h3>
                <a href="/for-families">Learn More for Families</a> {/* Link to future dedicated page */}
                <a href="mailto:families@familiarvoices.app?subject=Inquiry about Familiar Voices for Family Use">Contact for Family Use</a>
              </div>
              <div className="footer-section">
                <h3>Company</h3>
                <a href="#about">About Us</a>
                <a href="#faq">FAQ for Facilities</a>
              </div>
              <div className="footer-section">
                <h3>Contact & Connect</h3>
                <a href="mailto:info@familiarvoices.app">info@familiarvoices.app</a>
                {/* Add your actual LinkedIn page URL */}
                <a href="YOUR_LINKEDIN_PROFILE_URL" target="_blank" rel="noopener noreferrer">LinkedIn</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} Familiar Voices. All rights reserved.</p>
            {/* Optional: <a href="/privacy">Privacy Policy</a> | <a href="/terms">Terms of Service</a> */}
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App; 